// src/components/Adviser/Tabs/LicenseCertificates/Pages/LicenseCertificatesSummaryPage.jsx

import React, { useEffect, useMemo, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate } from "react-router-dom";
import Tooltip from "../../../../DesignSystem/Tooltip/Tooltip";
import Sidebar from "../../../../DesignSystem/Sidebar";
import PrimaryButton from "../../../../DesignSystem/Buttons/PrimaryButton";
import SecondaryButton from "../../../../DesignSystem/Buttons/SecondaryButton";
import IconButton from "../../../../DesignSystem/Buttons/IconButton";
import StatusButton from "../../../../DesignSystem/Buttons/StatusButton";
import Tabs from "../../../../DesignSystem/Tabs/Tabs";
import Field from "../../../../DesignSystem/Inputs/Field";
import { PostCard } from "../../../Tabs/HelperFunctions/HelperFunctions";
import { ReactComponent as CustomSortIcon } from "../../../../../assets/icons/paraplanningDashboard/Sort.svg";
import { ReactComponent as CustomInfoIcon } from "../../../../../assets/icons/information.svg";
import { ReactComponent as FilterIcon } from "../../../../../assets/icons/information.svg";
import { ReactComponent as ArrowDropdownIcon } from "../../../../../assets/icons/paraplanningDashboard/ArrowDropdown.svg";
import {
  formatDateOnly,
  subscribeLicenseCertificateTableRows,
} from "../utils/licenseCertificateTables";
import SearchField from "../../../../DesignSystem/Inputs/SearchField";
import "../../../../../css/Adviser/LicenseCertificatesSummary.css";
import OutlinedStatusButton from "../../../../DesignSystem/Buttons/OutlinedStatusButtons";
import Breadcrumb from "../../../../DesignSystem/Breadcrumb/Breadcrumb";
import { useCanEditLicenceTabs } from "../utils/accessPermissions";
import {
  buildCompetentAdviserTableRows,
  downloadFitAndProperExcel,
  downloadLicenseCertificatesExcel,
} from "../utils/exportLicenseCertificatesExcel";
import {
  COMPETENT_ADVISER_TEST_FIELDS as COMPETENT_ADVISER_TABLE_FIELDS,
  canViewCreditHistory,
} from "../utils/LicenseCertificateFields";
import CheckboxGroup from "../../../../DesignSystem/Inputs/CheckboxGroup";
import { useUserPermissions } from "../../../../SignIn/UserPermissionsContext";
import { fetchCpdAutomationLogs } from "../utils/cpdAutomationLogs";
import ProgressHistory from "../../../../Marketing/MarketingDashabord/components/ProgressHistory";

const EMPTY_VALUE = "-";
const LICENCE_SUMMARY_STATE_KEY = "licenceCertificatesSummary:viewState";
const LICENCE_SUMMARY_STATE_VERSION = 2;
const DEFAULT_ADVISER_STATUSES = ["FP", "MA", "GI/Prot", "NAP"];

const getSavedSummaryState = () => {
  try {
    const savedState = JSON.parse(
      sessionStorage.getItem(LICENCE_SUMMARY_STATE_KEY) || "{}",
    );

    // Status options changed in version 2. Use the new inclusive defaults once
    // for older sessions so NAP advisers are not kept hidden by stale filters.
    if (savedState.version !== LICENCE_SUMMARY_STATE_VERSION) {
      return {
        ...savedState,
        selectedAdviserStatuses: DEFAULT_ADVISER_STATUSES,
      };
    }

    return savedState;
  } catch {
    return {};
  }
};

const filterLicenceRows = ({
  rows = [],
  searchTerm = "",
  selectedAdviserStatuses = [],
}) => {
  const term = String(searchTerm || "")
    .trim()
    .toLowerCase();

  const selectedStatusSet = new Set(
    selectedAdviserStatuses.map((status) =>
      String(status || "")
        .trim()
        .toUpperCase(),
    ),
  );

  return rows.filter((row) => {
    const adviserStatus = String(row.adviserBusinessStatus || "")
      .trim()
      .toUpperCase();

    if (!selectedStatusSet.has(adviserStatus)) {
      return false;
    }

    if (!term) {
      return true;
    }

    const searchableText = [
      row.adviserName,
      row.adviserEmail,
      row.adviserBusinessStatus,
      row.category,
      row.deadlineLabel,
      row.testName,
      row.dplName,
      row.dplRegistrationNumber,
      row.spsAccreditedBody,
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();

    return searchableText.includes(term);
  });
};

const ADVISER_STATUS_OPTIONS = [
  {
    label: "FP",
    value: "FP",
  },
  {
    label: "MA",
    value: "MA",
  },
  {
    label: "GI/Prot",
    value: "GI/Prot",
  },
  {
    label: "NAP",
    value: "NAP",
  },
];

const getStatusDisplay = (row) => {
  if (row.statusClass === "expired") {
    return {
      label: "Expired",
      color: "red",
    };
  }

  if (row.statusClass === "noDate") {
    return {
      label: "No Date",
      color: "grey",
    };
  }

  if (row.statusClass === "dueSoon") {
    if (row.lastFollowUpSentAt || row.lastFollowUp !== "-") {
      return {
        label: "Follow Up Sent",
        color: "blue",
      };
    }

    return {
      label: "Expiring Soon",
      color: "yellow",
    };
  }

  return {
    label: "Active",
    color: "green",
  };
};

const getTooltipSourceLabel = (row) => {
  return (
    row.statusSourceLabel ||
    row.testName ||
    row.deadlineLabel ||
    row.category ||
    "This record"
  );
};

const getStatusTooltip = (row) => {
  const display = getStatusDisplay(row);
  const sourceLabel = getTooltipSourceLabel(row);

  if (display.label === "Active") {
    return [
      row.updatedLabel || `${sourceLabel}\nNo update details found.`,
      row.lastFollowUpSentAt || row.lastFollowUp !== "-"
        ? row.lastFollowUpLabel
        : "",
    ]
      .filter(Boolean)
      .join("\n");
  }

  if (display.label === "Follow Up Sent") {
    return row.lastFollowUpLabel || `${sourceLabel}\nFollow-up has been sent.`;
  }

  if (display.label === "Expired") {
    return [
      sourceLabel,
      `Expired deadline: ${formatDateOnly(row.deadline)}`,
      row.lastFollowUpLabel || "No follow-up has been sent yet.",
    ]
      .filter(Boolean)
      .join("\n");
  }

  if (display.label === "Expiring Soon") {
    const isWithinOneWeek =
      typeof row.daysUntil === "number" &&
      row.daysUntil >= 0 &&
      row.daysUntil <= 7;

    return [
      sourceLabel,
      `Deadline: ${formatDateOnly(row.deadline)}`,
      `Days remaining: ${row.daysUntil}`,
      row.lastFollowUpLabel || "No follow-up has been sent yet.",
      isWithinOneWeek
        ? row.nextFollowUpSendAt
          ? `Next follow-up will be sent at 08:00 on ${formatDateOnly(
              row.nextFollowUpSendAt,
            )}`
          : "No further scheduled follow-up before the deadline."
        : "",
    ]
      .filter(Boolean)
      .join("\n");
  }

  return `${sourceLabel}\nNo valid date has been stored for this record.`;
};

const InfoIcon = ({ tooltip }) => {
  if (!tooltip) return null;

  return (
    <Tooltip
      position="left"
      content={<span className="FP_Help_Text white-text">{tooltip}</span>}
    >
      <CustomInfoIcon className="lcInfoIcon" />
    </Tooltip>
  );
};

const StatusCell = ({ row }) => {
  const status = getStatusDisplay(row);

  return (
    <div className="lcStatusCell">
      <OutlinedStatusButton label={status.label} color={status.color} />
      <InfoIcon tooltip={getStatusTooltip(row)} />
    </div>
  );
};

const formatCpdTooltipHours = (value) =>
  `${Number(value || 0).toFixed(2)}h`;

const CpdTooltipContent = ({ details }) => (
  <span className="lcCpdTooltipContent FP_Help_Text white-text">
    <span className="FP_Help_Text_Semibold white-text">
      {details.adviserType}
    </span>
    <span>CPD timeline: {details.timeline}</span>
    <span className="lcCpdTooltipTable" role="table">
      <span role="columnheader" aria-label="Measure" />
      <span role="columnheader">Structured</span>
      <span role="columnheader">IDD</span>
      <span role="columnheader">Total</span>

      {[
        ["Expected", details.expected],
        ["Done", details.done],
        ["Remaining", details.remaining],
      ].map(([label, values]) => (
        <React.Fragment key={label}>
          <span role="rowheader">{label}</span>
          <span role="cell">{formatCpdTooltipHours(values.structured)}</span>
          <span role="cell">{formatCpdTooltipHours(values.idd)}</span>
          <span role="cell">{formatCpdTooltipHours(values.total)}</span>
        </React.Fragment>
      ))}
    </span>
  </span>
);

const CpdStatusCell = ({ row, isExpanded, onToggleExpanded }) => (
  <div className="lcStatusCell lcCpdStatusCell">
    <OutlinedStatusButton
      label={row.cpdStatus || "Incomplete"}
      color={row.cpdStatusColor || "grey"}
    />
    <InfoIcon
      tooltip={
        row.cpdStatusDetails ? (
          <CpdTooltipContent details={row.cpdStatusDetails} />
        ) : (
          row.cpdStatusTooltip
        )
      }
    />
    {onToggleExpanded ? (
      <IconButton
        className={`lcRowExpandButton ${isExpanded ? "is-expanded" : ""}`}
        icon={<ArrowDropdownIcon aria-hidden="true" />}
        label={`${isExpanded ? "Hide" : "Show"} CPD email and check logs`}
        onClick={onToggleExpanded}
      />
    ) : null}
  </div>
);

const DateCell = ({ value, highlightOlderThanMonths, skipHighlight }) => {
  // if (skipHighlight) {
  //   console.log("DateCell value:", value, "highlightOlderThanMonths:", highlightOlderThanMonths, "skipHighlight:", skipHighlight);
  // }
  const formatted = formatDateOnly(value);

  const shouldHighlight =
    highlightOlderThanMonths &&
    isAtLeastMonthsOld(value, highlightOlderThanMonths) &&
    !skipHighlight;

  return (
    <span
      className={`lcDateBadge FP_Help_Text black-text ${shouldHighlight ? "lcDateBadgeOld" : ""}`}
    >
      {formatted || EMPTY_VALUE}
    </span>
  );
};

const TextCell = ({
  value,
  textColor,
  row,
  onAdviserClick,
  clickable = false,
}) => {
  const handleClick = (event) => {
    event.stopPropagation();
    if (!clickable || !onAdviserClick) return;
    onAdviserClick(row);
  };

  const handleKeyDown = (event) => {
    if (!clickable || !onAdviserClick) return;

    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      event.stopPropagation();
      onAdviserClick(row);
    }
  };

  return (
    <span
      className={`FP_Help_Text ${textColor || ""} ${
        clickable ? "lcClickableTextCell" : ""
      }`}
      role={clickable ? "button" : undefined}
      tabIndex={clickable ? 0 : undefined}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
    >
      {value || EMPTY_VALUE}
    </span>
  );
};

const ReminderDates = ({ row }) => {
  if (!row.reminderDates?.length) return EMPTY_VALUE;

  return row.reminderDates.map(formatDateOnly).join(", ");
};

const SortIcon = ({ active, direction }) => {
  return (
    <span className="lcSortIcon">
      <CustomSortIcon />
    </span>
  );
  //   if (!active)

  //   return <span className="lcSortIcon">{direction === "asc" ? "↑" : "↓"}</span>;
};

// const COMPETENT_ADVISER_TABLE_FIELDS = [
//   {
//     key: "rriarTest",
//     label: "Regulated Retail Investment Adviser Re-Evaluation (RRIAR) Test",
//   },
//   {
//     key: "vct",
//     label: "VCT Test",
//   },
//   {
//     key: "longTermCareTest",
//     label: "Long Term Care Test",
//   },
//   {
//     key: "equityReleaseTest",
//     label: "Equity Release Test",
//   },
//   {
//     key: "consumerDutyTest",
//     label: "Consumer Duty Test",
//     accountsForExpiry: false,
//   },
// ];

const STATUS_SORT_ORDER = {
  Expired: 1,
  "Expiring Soon": 2,
  "Follow Up Sent": 3,
  Active: 4,
  "No Date": 5,
};

const getSortValue = (row, key) => {
  if (key === "status") {
    const status = getStatusDisplay(row);
    return STATUS_SORT_ORDER[status.label] || 99;
  }

  return row?.[key];
};

// const buildCompetentAdviserTableRows = (rows = []) => {
//   const groupedRows = new Map();

//   rows.forEach((row) => {
//     const groupKey = row.adviserEmail || row.adviserName || row.id;

//     if (!groupedRows.has(groupKey)) {
//       groupedRows.set(groupKey, {
//         id: `competent-adviser-${groupKey}`,
//         adviserName: row.adviserName,
//         adviserEmail: row.adviserEmail,
//         category: "Competent Adviser",
//         status: "No date",
//         statusClass: "noDate",
//         daysUntil: null,
//         deadline: "",
//         reminderDates: [],
//         lastFollowUp: "-",
//         lastFollowUpLabel: "No follow-up has been sent yet.",
//         updatedLabel: row.updatedLabel,
//       });
//     }

//     const adviserRow = groupedRows.get(groupKey);

//     adviserRow[row.testKey] = row.deadline;
//     adviserRow[`${row.testKey}Meta`] = row;
//   });

//   groupedRows.forEach((adviserRow) => {
//     const visibleTestRows = COMPETENT_ADVISER_TABLE_FIELDS.filter(
//       (field) => field.accountsForExpiry !== false,
//     )
//       .map((field) => adviserRow[`${field.key}Meta`])
//       .filter(Boolean);

//     const urgentRows = visibleTestRows
//       .filter((row) => typeof row.daysUntil === "number")
//       .sort((a, b) => a.daysUntil - b.daysUntil);

//     const statusSource = urgentRows[0] || visibleTestRows[0];

//    if (statusSource) {
//      adviserRow.status = statusSource.status;
//      adviserRow.statusClass = statusSource.statusClass;
//      adviserRow.daysUntil = statusSource.daysUntil;
//      adviserRow.deadline = statusSource.deadline;
//      adviserRow.reminderDates = statusSource.reminderDates;
//      adviserRow.lastFollowUp = statusSource.lastFollowUp;
//      adviserRow.lastFollowUpSentAt = statusSource.lastFollowUpSentAt;
//      adviserRow.lastFollowUpLabel = statusSource.lastFollowUpLabel;
//      adviserRow.lastFollowUpPeriod = statusSource.lastFollowUpPeriod;
//      adviserRow.lastFollowUpSubject = statusSource.lastFollowUpSubject;
//      adviserRow.lastFollowUpFromName = statusSource.lastFollowUpFromName;
//      adviserRow.lastFollowUpFromEmail = statusSource.lastFollowUpFromEmail;
//      adviserRow.nextFollowUpSendAt = statusSource.nextFollowUpSendAt;
//      adviserRow.updatedLabel = statusSource.updatedLabel;
//      adviserRow.statusSourceLabel =
//        statusSource.statusSourceLabel ||
//        statusSource.testName ||
//        statusSource.deadlineLabel ||
//        "Competent Adviser";
//    }
//   });

//   return Array.from(groupedRows.values());
// };

const formatAutomationHours = (value) => {
  const parsedValue = Number(value);
  return Number.isFinite(parsedValue)
    ? `${parsedValue.toFixed(2)}h`
    : EMPTY_VALUE;
};

const getAutomationRecipient = (log) => {
  if (log.recipient) return log.recipient;
  if (Array.isArray(log.recipients) && log.recipients.length) {
    return log.recipients.join(", ");
  }
  return EMPTY_VALUE;
};

const getAutomationHistoryLabel = (log) => {
  const status = String(log.status || "Unknown").replaceAll("_", " ");
  if (log.eventType === "email_attempt") return `Email ${status}`;
  if (log.eventType === "cpd_check") return `CPD check: ${status}`;
  return `CPD activity: ${status}`;
};

const getAutomationHistoryTone = (log) => {
  const status = String(log.status || "").toLowerCase();
  if (["sent", "on_track", "completed", "success"].includes(status)) {
    return "green";
  }
  if (status === "behind") return "orange";
  if (status === "failed" || status === "error") return "orange-600";
  if (status.startsWith("skipped_")) return "grey";
  return "blue";
};

const getAutomationHistoryItems = (logs) =>
  logs.map((log) => {
    const timestamp =
      log.sentTimestamp || log.attemptedAt || log.occurredAt || "";
    const timestampMs = new Date(timestamp).getTime();
    const recipient = getAutomationRecipient(log);
    const description = [
      log.quarterKey || log.quarter
        ? `Quarter: ${log.quarterKey || log.quarter}`
        : "",
      log.subject ? `Subject: ${log.subject}` : "",
      `Expected total: ${formatAutomationHours(log.expectedCpdHours)} | Actual total: ${formatAutomationHours(log.actualCpdHours)}`,
      `Remaining structured: ${formatAutomationHours(log.remainingStructuredHours)} | Remaining IDD: ${formatAutomationHours(log.remainingIddHours)}`,
    ]
      .filter(Boolean)
      .join("\n");

    return {
      id: log.id,
      label: getAutomationHistoryLabel(log),
      tone: getAutomationHistoryTone(log),
      createdAtMs: Number.isNaN(timestampMs) ? undefined : timestampMs,
      userEmail: recipient === EMPTY_VALUE ? "" : `To: ${recipient}`,
      description,
      notes: log.error || "",
    };
  });

const CpdAutomationLogsPanel = ({ row }) => {
  const [logs, setLogs] = useState([]);
  const [loadingLogs, setLoadingLogs] = useState(true);
  const [logsError, setLogsError] = useState("");
  const historyItems = useMemo(() => getAutomationHistoryItems(logs), [logs]);

  useEffect(() => {
    let active = true;
    setLoadingLogs(true);
    setLogsError("");

    fetchCpdAutomationLogs(row.adviserId)
      .then((records) => {
        if (active) setLogs(records);
      })
      .catch((error) => {
        if (active) {
          setLogs([]);
          setLogsError(error.message || "Unable to load CPD automation logs.");
        }
      })
      .finally(() => {
        if (active) setLoadingLogs(false);
      });

    return () => {
      active = false;
    };
  }, [row.adviserId]);

  return (
    <div
      className="lcAutomationLogsPanel"
      onClick={(event) => event.stopPropagation()}
    >
      {logsError ? (
        <div className="adviserLicenseError">{logsError}</div>
      ) : (
        <ProgressHistory
          items={historyItems}
          loading={loadingLogs}
          title="CPD email and check logs"
          ariaLabel={`CPD email and check logs for ${row.adviserName}`}
          emptyMessage="No CPD checks or email attempts have been logged for this adviser yet."
          showDuration={false}
          tooltipPosition="right"
        />
      )}
    </div>
  );
};

const SummaryTable = ({
  columns,
  rows,
  resetSortSignal,
  onAdviserClick,
  restoreAdviserKey,
  renderExpandedRow,
}) => {
  const [sortConfig, setSortConfig] = useState(DEFAULT_SORT_CONFIG);
  const [expandedRowId, setExpandedRowId] = useState("");
  const canEditLicenceTabs = useCanEditLicenceTabs();
  const rowRefs = useRef(new Map());
  const restoredAdviserRef = useRef("");
  useEffect(() => {
    setSortConfig(DEFAULT_SORT_CONFIG);
    setExpandedRowId("");
  }, [resetSortSignal]);

  const sortedRows = useMemo(() => {
    const list = [...rows];

    list.sort((a, b) => {
      const aValue = getSortValue(a, sortConfig.key);
      const bValue = getSortValue(b, sortConfig.key);

      if (aValue === null || aValue === undefined || aValue === "") return 1;
      if (bValue === null || bValue === undefined || bValue === "") return -1;

      if (typeof aValue === "number" && typeof bValue === "number") {
        return sortConfig.direction === "asc"
          ? aValue - bValue
          : bValue - aValue;
      }

      return sortConfig.direction === "asc"
        ? String(aValue).localeCompare(String(bValue))
        : String(bValue).localeCompare(String(aValue));
    });

    return list;
  }, [rows, sortConfig]);

  useEffect(() => {
    if (!restoreAdviserKey || restoredAdviserRef.current === restoreAdviserKey) {
      return undefined;
    }

    const rowElement = rowRefs.current.get(restoreAdviserKey);
    if (!rowElement) return undefined;

    let restoreFrame;
    const frame = window.requestAnimationFrame(() => {
      restoreFrame = window.requestAnimationFrame(() => {
        rowElement.scrollIntoView({ block: "center", behavior: "auto" });
        restoredAdviserRef.current = restoreAdviserKey;
      });
    });

    return () => {
      window.cancelAnimationFrame(frame);
      if (restoreFrame) window.cancelAnimationFrame(restoreFrame);
    };
  }, [restoreAdviserKey, sortedRows]);

  const handleSort = (column) => {
    if (column.sortable === false) return;

    setSortConfig((prev) => {
      if (prev.key === column.key) {
        return {
          key: column.key,
          direction: prev.direction === "asc" ? "desc" : "asc",
        };
      }

      return {
        key: column.key,
        direction: "asc",
      };
    });
  };

  const getPinnedColumnClass = (column) => {
    if (column.key === "adviserName") return "lcPinnedColumn lcPinnedColumnLeft";
    if (column.key === "status") return "lcPinnedColumn lcPinnedColumnRight";
    return "";
  };

  return (
    <div className="lcTableWrap">
      <table className="lcTable">
        <thead>
          <tr>
            <th
              className="lcIndexColumn lcPinnedColumn"
              aria-label="Row number"
            />
            {columns.map((column) => (
              <th
                key={column.key}
                onClick={() => handleSort(column)}
                className={[
                  column.sortable === false ? "" : "lcSortableHead",
                  getPinnedColumnClass(column),
                ]
                  .filter(Boolean)
                  .join(" ")}
              >
                <div className="ParaplanningTableHeaderButton">
                  <span className="FP_Help_Text_Semibold">{column.label}</span>
                  {column.sortable === false ? null : (
                    <SortIcon
                      active={sortConfig.key === column.key}
                      direction={sortConfig.direction}
                    />
                  )}
                </div>
              </th>
            ))}
          </tr>
        </thead>

        <tbody>
          {sortedRows.length ? (
            sortedRows.map((row, index) => {
              const isExpanded = expandedRowId === row.id;
              return (
                <React.Fragment key={row.id}>
                  <tr
                    ref={(element) => {
                      const adviserKey =
                        row.adviserEmail || row.adviserId || row.id;
                      if (element) rowRefs.current.set(adviserKey, element);
                      else rowRefs.current.delete(adviserKey);
                    }}
                    onClick={() =>
                      onAdviserClick &&
                      canEditLicenceTabs &&
                      onAdviserClick(row)
                    }
                    style={{
                      cursor:
                        onAdviserClick && canEditLicenceTabs
                          ? "pointer"
                          : "default",
                    }}
                  >
                    <td className="lcIndexColumn lcPinnedColumn">
                      <TextCell
                        value={`${index + 1}.`}
                        textColor="primary-text"
                      />
                    </td>
                    {columns.map((column) => (
                      <td
                        key={`${row.id}-${column.key}`}
                        className={getPinnedColumnClass(column)}
                      >
                        {column.render ? (
                          column.render(row, {
                            isExpanded,
                            onToggleExpanded: renderExpandedRow
                              ? (event) => {
                                  event.stopPropagation();
                                  setExpandedRowId((currentRowId) =>
                                    currentRowId === row.id ? "" : row.id,
                                  );
                                }
                              : null,
                          })
                        ) : (
                          <TextCell
                            value={row[column.key]}
                            row={row}
                            textColor={
                              column.key === "adviserName"
                                ? "primary-text"
                                : "black"
                            }
                            // clickable={true}
                            clickable={column.key === "adviserName"}
                            onAdviserClick={
                              canEditLicenceTabs ? onAdviserClick : null
                            }
                          />
                        )}
                      </td>
                    ))}
                  </tr>
                  {isExpanded ? (
                    <tr className="lcExpandedLogRow">
                      <td
                        className="lcIndexColumn lcPinnedColumn"
                        aria-hidden="true"
                      />
                      <td
                        className="lcPinnedColumn lcPinnedColumnLeft"
                        aria-hidden="true"
                      />
                      <td colSpan={Math.max(columns.length - 1, 1)}>
                        {renderExpandedRow(row)}
                      </td>
                    </tr>
                  ) : null}
                </React.Fragment>
              );
            })
          ) : (
            <tr>
              <td colSpan={columns.length + 1} className="lcEmptyCell">
                No records found.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

const allColumns = [
  { key: "adviserName", label: "Adviser Name" },
  { key: "adviserEmail", label: "Email" },
  { key: "category", label: "Certificate" },
  { key: "deadlineLabel", label: "Deadline Type" },
  {
    key: "deadline",
    label: "Deadline",
    render: (row) => <DateCell value={row.deadline} />,
  },
  {
    key: "status",
    label: "Status",

    render: (row) => <StatusCell row={row} />,
  },
];

const dplColumns = [
  { key: "adviserName", label: "Adviser Name" },
  { key: "dplName", label: "DPL Name" },
  { key: "dplRegistrationNumber", label: "DPL Reg No." },
  {
    key: "deadline",
    label: "DPL Expiry Date",
    render: (row) => <DateCell value={row.deadline} />,
  },
  {
    key: "status",
    label: "Status",

    render: (row) => <StatusCell row={row} />,
  },
];

const spsColumns = [
  { key: "adviserName", label: "Adviser Name" },
  { key: "spsAccreditedBody", label: "SPS Accredited Body" },
  {
    key: "deadline",
    label: "SPS Expiry Date",
    render: (row) => <DateCell value={row.deadline} />,
  },
  {
    key: "status",
    label: "Status",

    render: (row) => <StatusCell row={row} />,
  },
];

const fitColumns = [
  { key: "adviserName", label: "Adviser Name" },
  {
    key: "deadline",
    label: "Fit & Proper Expiry Date",
    render: (row) => <DateCell value={row.deadline} />,
  },
  {
    key: "status",
    label: "Status",

    render: (row) => <StatusCell row={row} />,
  },
];

const creditHistoryColumns = [
  { key: "adviserName", label: "Adviser Name" },
  { key: "creditScore", label: "Credit Score" },
  {
    key: "creditScoreDate",
    label: "Credit Score Date",
    render: (row) => <DateCell value={row.creditScoreDate} />,
  },
  {
    key: "creditReportDate",
    label: "Credit Report Date",
    render: (row) => <DateCell value={row.creditReportDate} />,
  },
];

const parseDateValue = (value) => {
  if (!value) return null;

  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return value;
  }

  if (typeof value === "string") {
    const trimmed = value.trim();

    if (!trimmed || trimmed === "-") return null;

    // Handles YYYY-MM-DD
    if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
      const [year, month, day] = trimmed.split("-").map(Number);
      return new Date(year, month - 1, day);
    }

    // Handles DD/MM/YYYY
    if (/^\d{2}\/\d{2}\/\d{4}$/.test(trimmed)) {
      const [day, month, year] = trimmed.split("/").map(Number);
      return new Date(year, month - 1, day);
    }

    const parsed = new Date(trimmed);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  return null;
};

const isAtLeastMonthsOld = (value, months = 11) => {
  const date = parseDateValue(value);

  if (!date) return false;

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const threshold = new Date(today);
  threshold.setMonth(threshold.getMonth() - months);

  return date <= threshold;
};

const competentColumns = [
  { key: "adviserName", label: "Adviser Name" },

  ...COMPETENT_ADVISER_TABLE_FIELDS.map((field) => ({
    key: field.key,
    label: field.label,
    render: (row) => (
      <DateCell
        value={row[field.key]}
        highlightOlderThanMonths={11}
        skipHighlight={field.key === "consumerDutyTest"}
      />
    ),
  })),

  {
    key: "status",
    label: "Status",
    render: (row) => <StatusCell row={row} />,
  },
];

const tcColumns = [
  { key: "adviserName", label: "Adviser Name" },
  {
    key: "appointmentDate",
    label: "Appointment Date",
    render: (row) => <DateCell value={row.appointmentDate} />,
  },
  {
    key: "preCASSignOff",
    label: "Pre-CAS Sign Off",
    render: (row) => <DateCell value={row.preCASSignOff} />,
  },
  {
    key: "lastOcaMeeting",
    label: "Last OCA Meeting",
    render: (row) => <DateCell value={row.lastOcaMeeting} />,
  },
  {
    key: "nextOcaMeeting",
    label: "Next OCA Meeting",
    render: (row) => <DateCell value={row.nextOcaMeeting} />,
  },
  {
    key: "status",
    label: "Status",

    render: (row) => <StatusCell row={row} />,
  },
];

const allCpdColumns = [
  { key: "adviserName", label: "Adviser Name" },
  {
    key: "reportDate",
    label: "Report Date",
    render: (row) => <DateCell value={row.reportDate} />,
  },
  { key: "totalCpdHours", label: "Total CPD Hours" },
  { key: "structuredHours", label: "Structured Hours" },
  { key: "nonStructuredHours", label: "Non-Structured Hours" },
  {
    key: "cpdStatus",
    label: "Status",
    render: (row, expandControls) => (
      <CpdStatusCell row={row} {...expandControls} />
    ),
  },
];

const iddCpdColumns = [
  { key: "adviserName", label: "Adviser Name" },
  {
    key: "reportDate",
    label: "Report Date",
    render: (row) => <DateCell value={row.reportDate} />,
  },
  { key: "iddHours", label: "Total IDD Hours" },
  { key: "structuredHours", label: "Structured Hours" },
  { key: "nonStructuredHours", label: "Non-Structured Hours" },
  {
    key: "cpdStatus",
    label: "Status",
    render: (row, expandControls) => (
      <CpdStatusCell row={row} {...expandControls} />
    ),
  },
];

const DEFAULT_SORT_CONFIG = {
  key: "daysUntil",
  direction: "asc",
};
export default function LicenseCertificatesSummaryPage() {
  const navigate = useNavigate();
  const canEditLicenceTabs = useCanEditLicenceTabs();
  const { userPermissions = [] } = useUserPermissions();
  const canViewCredit = useMemo(
    () => canViewCreditHistory(userPermissions),
    [userPermissions],
  );
  const savedSummaryState = useMemo(getSavedSummaryState, []);
  const scrollRestoredRef = useRef(false);
  const lastAdviserKeyRef = useRef(savedSummaryState.lastAdviserKey || "");
  const [resetSortSignal, setResetSortSignal] = useState(0);
  const [activeTab, setActiveTab] = useState(
    savedSummaryState.activeTab === "cpd"
      ? "allCpd"
      : savedSummaryState.activeTab || "dpl",
  );
  const [searchTerm, setSearchTerm] = useState(
    savedSummaryState.searchTerm || "",
  );
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [data, setData] = useState(null);
  const [showActions, setShowActions] = useState(false);
  const [selectedAdviserStatuses, setSelectedAdviserStatuses] = useState(
    Array.isArray(savedSummaryState.selectedAdviserStatuses)
      ? savedSummaryState.selectedAdviserStatuses
      : DEFAULT_ADVISER_STATUSES,
  );

  const handleResetFilters = () => {
    setResetSortSignal((prev) => prev + 1);
    setSearchTerm("");
    setSelectedAdviserStatuses(DEFAULT_ADVISER_STATUSES);
  };

  useEffect(() => {
    const saveViewState = () => {
      sessionStorage.setItem(
        LICENCE_SUMMARY_STATE_KEY,
        JSON.stringify({
          version: LICENCE_SUMMARY_STATE_VERSION,
          activeTab,
          searchTerm,
          selectedAdviserStatuses,
          lastAdviserKey: lastAdviserKeyRef.current,
          scrollY: scrollRestoredRef.current
            ? window.scrollY
            : Number(savedSummaryState.scrollY || 0),
        }),
      );
    };

    window.addEventListener("scroll", saveViewState, { passive: true });
    saveViewState();

    return () => {
      saveViewState();
      window.removeEventListener("scroll", saveViewState);
    };
  }, [activeTab, searchTerm, selectedAdviserStatuses, savedSummaryState]);

  const handleDownloadExcel = () => {
    try {
      setLoadError("");
      downloadLicenseCertificatesExcel(data, {
        includeCreditHistory: canViewCredit,
      });
    } catch (error) {
      console.error("Failed to download licence certificate Excel:", error);

      setLoadError(
        error.message || "Failed to download the licence certificate data.",
      );
    }
  };

  const handleDownloadFitAndProper = () => {
    try {
      setLoadError("");
      downloadFitAndProperExcel(data?.fitAndProperRows || []);
    } catch (error) {
      console.error("Failed to download Fit & Proper Excel:", error);
      setLoadError(error.message || "Failed to download Fit & Proper data.");
    }
  };

  const SECTION_BY_TAB = {
    dpl: "dplCertificate",
    sps: "spsCertificate",
    fitAndProper: "fitAndProper",
    creditHistory: "fitAndProper",
    competentAdviser: "competentAdviser",
    tc: "tc",
    allCpd: "cpd",
    iddCpd: "cpd",
  };

  const handleAdviserClick = (row) => {
    const adviserKey = row.adviserId || row.adviserEmail || "";

    if (!adviserKey) return;

    lastAdviserKeyRef.current = adviserKey;
    sessionStorage.setItem(
      LICENCE_SUMMARY_STATE_KEY,
      JSON.stringify({
        ...getSavedSummaryState(),
        version: LICENCE_SUMMARY_STATE_VERSION,
        activeTab,
        searchTerm,
        selectedAdviserStatuses,
        lastAdviserKey: adviserKey,
        scrollY: window.scrollY,
      }),
    );

    navigate(
      `/advisers/${encodeURIComponent(adviserKey)}/license-certificates/edit`,
      {
        state: {
          adviserKey,
          adviserId: row.adviserId || "",
          adviserEmail: row.adviserEmail || "",
          adviserName: row.adviserName || "",
          scrollToSection: SECTION_BY_TAB[activeTab] || "dplCertificate",
          returnTo: "/advisers/licence-certificates/summary",
        },
      },
    );
  };

  // const loadData = async () => {
  //   try {
  //     setLoading(true);
  //     setLoadError("");

  //     const rows = await loadLicenseCertificateTableRows();
  //     setData(rows);
  //   } catch (error) {
  //     console.error("Failed to load licence certificate summary:", error);
  //     setLoadError(error.message || "Failed to load licence certificate data.");
  //   } finally {
  //     setLoading(false);
  //   }
  // };

  // useEffect(() => {
  //   loadData();
  // }, []);

  useEffect(() => {
    setLoading(true);
    setLoadError("");

    const unsubscribe = subscribeLicenseCertificateTableRows({
      onData: (rows) => {
        setData(rows);
        setLoading(false);
      },
      onError: (error) => {
        console.error("Failed to load licence certificate summary:", error);
        setLoadError(
          error.message || "Failed to load licence certificate data.",
        );
        setLoading(false);
      },
    });

    return unsubscribe;
  }, []);

  useEffect(() => {
    if (loading || scrollRestoredRef.current) return undefined;

    if (savedSummaryState.lastAdviserKey) {
      scrollRestoredRef.current = true;
      return undefined;
    }

    const scrollY = Number(savedSummaryState.scrollY || 0);
    let restoreFrame;
    const frame = window.requestAnimationFrame(() => {
      restoreFrame = window.requestAnimationFrame(() => {
        window.scrollTo(0, Number.isFinite(scrollY) ? scrollY : 0);
        scrollRestoredRef.current = true;
      });
    });

    return () => {
      window.cancelAnimationFrame(frame);
      if (restoreFrame) window.cancelAnimationFrame(restoreFrame);
    };
  }, [loading, savedSummaryState]);

  const rowsByTab = useMemo(
    () => ({
      dpl: data?.dplRows || [],
      sps: data?.spsRows || [],
      fitAndProper: data?.fitAndProperRows || [],
      creditHistory: data?.fitAndProperRows || [],
      competentAdviser: buildCompetentAdviserTableRows(
        data?.competentAdviserRows || [],
      ),
      tc: data?.tcRows || [],
      allCpd: data?.allCpdRows || [],
      iddCpd: data?.iddCpdRows || [],
      all: data?.allRows || [],
    }),
    [data],
  );

  const filteredRowsByTab = useMemo(() => {
    const applyFilters = (rows) =>
      filterLicenceRows({
        rows,
        searchTerm,
        selectedAdviserStatuses,
      });

    return {
      dpl: applyFilters(rowsByTab.dpl),
      sps: applyFilters(rowsByTab.sps),
      fitAndProper: applyFilters(rowsByTab.fitAndProper),
      creditHistory: applyFilters(rowsByTab.creditHistory),
      competentAdviser: applyFilters(rowsByTab.competentAdviser),
      tc: applyFilters(rowsByTab.tc),
      allCpd: applyFilters(rowsByTab.allCpd),
      iddCpd: applyFilters(rowsByTab.iddCpd),
      all: applyFilters(rowsByTab.all),
    };
  }, [rowsByTab, searchTerm, selectedAdviserStatuses]);

 const tabs = useMemo(
   () => [
     {
       id: "dpl",
       label: "DPL Certificate",
       count: filteredRowsByTab.dpl.length,
     },
     {
       id: "sps",
       label: "SPS Certificate",
       count: filteredRowsByTab.sps.length,
     },
     {
       id: "fitAndProper",
       label: "Fit & Proper",
       count: filteredRowsByTab.fitAndProper.length,
     },
     ...(canViewCredit
       ? [
           {
             id: "creditHistory",
             label: "Credit History",
             count: filteredRowsByTab.creditHistory.length,
           },
         ]
       : []),
     {
       id: "competentAdviser",
       label: "Competent Adviser",
       count: filteredRowsByTab.competentAdviser.length,
     },
     {
       id: "tc",
       label: "T&C",
       count: filteredRowsByTab.tc.length,
     },
     {
       id: "allCpd",
       label: "All CPD",
       count: filteredRowsByTab.allCpd.length,
     },
     {
       id: "iddCpd",
       label: "IDD",
       count: filteredRowsByTab.iddCpd.length,
     },
   ],
   [canViewCredit, filteredRowsByTab],
 );
const currentTable = useMemo(() => {
  switch (activeTab) {
    case "sps":
      return {
        title: "SPS Certificate",
        rows: filteredRowsByTab.sps,
        columns: spsColumns,
      };

    case "fitAndProper":
      return {
        title: "Fit & Proper",
        rows: filteredRowsByTab.fitAndProper,
        columns: fitColumns,
      };

    case "creditHistory":
      return {
        title: "Credit History",
        rows: canViewCredit ? filteredRowsByTab.creditHistory : [],
        columns: creditHistoryColumns,
      };

    case "competentAdviser":
      return {
        title: "Competent Adviser",
        rows: filteredRowsByTab.competentAdviser,
        columns: competentColumns,
      };

    case "tc":
      return {
        title: "T&C",
        rows: filteredRowsByTab.tc,
        columns: tcColumns,
      };

    case "allCpd":
      return {
        title: "All CPD",
        rows: filteredRowsByTab.allCpd,
        columns: allCpdColumns,
      };

    case "iddCpd":
      return {
        title: "IDD CPD",
        rows: filteredRowsByTab.iddCpd,
        columns: iddCpdColumns,
      };

    case "all":
      return {
        title: "All Licence Certificate Deadlines",
        rows: filteredRowsByTab.all,
        columns: allColumns,
      };

    case "dpl":
    default:
      return {
        title: "DPL Certificate",
        rows: filteredRowsByTab.dpl,
        columns: dplColumns,
      };
  }
}, [activeTab, canViewCredit, filteredRowsByTab]);
  
const filteredRows = useMemo(() => {
  const term = String(searchTerm || "")
    .trim()
    .toLowerCase();

  const selectedStatusSet = new Set(
    selectedAdviserStatuses.map((status) =>
      String(status || "")
        .trim()
        .toUpperCase(),
    ),
  );

  return currentTable.rows.filter((row) => {
    const adviserStatus = String(row.adviserBusinessStatus || "")
      .trim()
      .toUpperCase();

    if (!selectedStatusSet.has(adviserStatus)) {
      return false;
    }

    if (!term) {
      return true;
    }

    const searchableText = [
      row.adviserName,
      row.adviserEmail,
      row.adviserBusinessStatus,
      row.category,
      row.deadlineLabel,
      row.testName,
      row.dplName,
      row.dplRegistrationNumber,
      row.spsAccreditedBody,
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();

    return searchableText.includes(term);
  });
}, [currentTable.rows, searchTerm, selectedAdviserStatuses]);
  
  return (
    <div className="main-content lcSummaryShell">
      <Helmet>
        <title>Licence Certificates</title>
      </Helmet>

      <Sidebar isSetExpanded={true} />

      <div className="Main-Container">
        <div className="lcSummaryPage">
          <Breadcrumb
            items={[
              { label: "Compliance", to: "/Department/Compliance" },
              { label: "Licence Certificates" },
            ]}
          />
          <div className="lcSummaryHeader">
            <div className="lcSummaryTitleWrap">
              <div>
                <h3 className="FP_H3">Licence Certificates</h3>
                <p className="Content-Small muted">
                  Review adviser certificate dates and follow-up statuses.
                </p>
              </div>
            </div>

            <div className="lcSummaryActions lcSummaryActionsMenu">
              <IconButton
                icon={<FilterIcon aria-hidden="true" />}
                label={showActions ? "Hide actions" : "Show actions"}
                onClick={() => setShowActions((current) => !current)}
              />
              {showActions ? (
                <div className="lcSummaryActionPanel">
                  {canEditLicenceTabs ? (
                    <SecondaryButton
                      label="Upload CPD Reports"
                      onClick={() =>
                        navigate("/compliance/license-certificates/cpd-records")
                      }
                    />
                  ) : null}
                  <PrimaryButton
                    label="Download Excel"
                    onClick={handleDownloadExcel}
                    disabled={loading || !data}
                  />
                  {canViewCredit ? (
                    <PrimaryButton
                      label="Credit History"
                      onClick={handleDownloadFitAndProper}
                      disabled={loading || !data}
                    />
                  ) : null}
                </div>
              ) : null}
            </div>
          </div>

          {/* {loadError ? (
            <div className="adviserLicenseError Content-Small">{loadError}</div>
          ) : null} */}

          {/* <div className="lcAdviserStatusFilter">
            <div className="ds-input-label">Adviser Status</div> */}
          <div className="row space-between">
            <CheckboxGroup
              options={ADVISER_STATUS_OPTIONS}
              selectedOptions={selectedAdviserStatuses}
              formDataKey="adviserStatus"
              onChange={setSelectedAdviserStatuses}
              columns={4}
              disabled={loading}
            />
            <div className="lcSummaryActions">
             
              <SearchField
                label=""
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                placeholder="Adviser Name"
              />
              {/* <SecondaryButton
                label="Reset Sort"
                onClick={handleResetFilters}
                disabled={loading}
              /> */}
            </div>
          </div>
          {/* </div> */}

          <Tabs
            tabs={tabs}
            activeTab={activeTab}
            onTabChange={setActiveTab}
            noShadow
          />

          <SummaryTable
            columns={currentTable.columns}
            rows={currentTable.rows}
            resetSortSignal={resetSortSignal}
            onAdviserClick={handleAdviserClick}
            restoreAdviserKey={savedSummaryState.lastAdviserKey || ""}
            renderExpandedRow={
              activeTab === "allCpd" || activeTab === "iddCpd"
                ? (row) => <CpdAutomationLogsPanel row={row} />
                : null
            }
          />
          {/* <PostCard title={currentTable.title}>
            {loading ? (
              <div className="Content-Small muted">Loading certificates…</div>
            ) : (
            )}
          </PostCard> */}
        </div>
      </div>
    </div>
  );
}
